import React, { useState } from 'react';
import { getCalendarStatus, getCalendarAuthUrl, createCalendarEvent } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const RdvGoogleButton = ({ dossierId, onCreated }) => {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        date_rdv: format(new Date(), 'yyyy-MM-dd'),
        heure_rdv: '09:00',
        duree_minutes: 60,
        notes: ''
    });

    const openDialog = async () => {
        try {
            const res = await getCalendarStatus();
            setStatus(res.data);
        } catch {
            setStatus({ connected: false });
        }
        setOpen(true);
    };

    const handleConnect = async () => {
        try {
            const res = await getCalendarAuthUrl();
            if (res.data?.auth_url) {
                window.location.href = res.data.auth_url;
            } else {
                toast.error(res.data?.message || 'Google Calendar non configuré');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Google Calendar indisponible');
        }
    };

    const handleCreate = async () => {
        setLoading(true);
        try {
            await createCalendarEvent({
                dossier_id: dossierId,
                ...form,
                duree_minutes: Number(form.duree_minutes) || 60
            });
            toast.success('RDV créé dans Google Agenda');
            setOpen(false);
            onCreated && onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur création RDV');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                variant="outline"
                onClick={openDialog}
                className="rounded-sm border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-white"
                data-testid="rdv-google-btn"
            >
                <CalendarIcon className="w-4 h-4 mr-2" /> Créer un RDV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="rdv-google-dialog">
                    <DialogHeader>
                        <DialogTitle>Créer un RDV — Google Agenda</DialogTitle>
                    </DialogHeader>
                    {!status?.connected ? (
                        <div className="space-y-3">
                            <p className="text-sm text-[#52525B]">
                                Connectez votre compte Google pour créer un RDV synchronisé avec Google Agenda.
                            </p>
                            <Button
                                onClick={handleConnect}
                                className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm w-full"
                                data-testid="connect-google-btn"
                            >
                                Connecter Google Agenda
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="field-label">Date</Label>
                                    <Input
                                        type="date"
                                        value={form.date_rdv}
                                        onChange={(e) => setForm({ ...form, date_rdv: e.target.value })}
                                        className="h-10 rounded-sm"
                                        data-testid="rdv-date-input"
                                    />
                                </div>
                                <div>
                                    <Label className="field-label">Heure</Label>
                                    <Input
                                        type="time"
                                        value={form.heure_rdv}
                                        onChange={(e) => setForm({ ...form, heure_rdv: e.target.value })}
                                        className="h-10 rounded-sm"
                                        data-testid="rdv-heure-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="field-label">Durée (min)</Label>
                                <Input
                                    type="number"
                                    value={form.duree_minutes}
                                    onChange={(e) => setForm({ ...form, duree_minutes: e.target.value })}
                                    className="h-10 rounded-sm"
                                    data-testid="rdv-duree-input"
                                />
                            </div>
                            <div>
                                <Label className="field-label">Notes</Label>
                                <Textarea
                                    rows={2}
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="rounded-sm"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annuler</Button>
                        {status?.connected && (
                            <Button
                                onClick={handleCreate}
                                disabled={loading}
                                className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                                data-testid="create-rdv-btn"
                            >
                                {loading ? 'Création...' : 'Créer le RDV'}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
